import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, List, Plus, Edit, Trash2, Clock, Users, Phone, MapPin, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const locales = {
  'es': es,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const Reservations = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('calendar'); // calendar or list
  const [showModal, setShowModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    date: '',
    time: '',
    people: 2,
    experience_type: 'almuerzo_carta',
    needs_transport: false,
    transport_pickup: '',
    transport_dropoff: '',
    message: '',
    status: 'pending'
  });

  const experienceTypes = [
    { value: 'almuerzo_carta', label: 'Almuerzo a la Carta' },
    { value: 'degustacion_sv', label: 'Degustación Single Vineyard' },
    { value: 'degustacion_premium', label: 'Degustación Premium' },
    { value: 'degustacion_sv_almuerzo', label: 'Degustación SV + Almuerzo' },
    { value: 'degustacion_premium_almuerzo', label: 'Degustación Premium + Almuerzo' },
  ];

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/reservations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setReservations(response.data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingReservation) {
        await axios.put(`${API}/admin/reservations/${editingReservation.id}`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/reservations`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      
      fetchReservations();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving reservation:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta reserva?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/reservations/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchReservations();
    } catch (error) {
      console.error('Error deleting reservation:', error);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(`${API}/reservations/${id}/status?status=${status}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchReservations();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleEdit = (reservation) => {
    setEditingReservation(reservation);
    setFormData({
      customer_name: reservation.customer_name,
      customer_email: reservation.customer_email || '',
      customer_phone: reservation.customer_phone,
      date: reservation.date,
      time: reservation.time,
      people: reservation.people,
      experience_type: reservation.experience_type,
      needs_transport: reservation.needs_transport || false,
      transport_pickup: reservation.transport_pickup || '',
      transport_dropoff: reservation.transport_dropoff || '',
      message: reservation.message || '',
      status: reservation.status
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingReservation(null);
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      date: '',
      time: '',
      people: 2,
      experience_type: 'almuerzo_carta',
      needs_transport: false,
      transport_pickup: '',
      transport_dropoff: '',
      message: '',
      status: 'pending'
    });
  };

  // Convert reservations to calendar events
  const events = reservations.map(res => ({
    id: res.id,
    title: `${res.customer_name} - ${res.people} personas`,
    start: new Date(`${res.date}T${res.time}`),
    end: new Date(`${res.date}T${res.time}`),
    resource: res
  }));

  const eventStyleGetter = (event) => {
    const statusColors = {
      pending: { backgroundColor: '#f59e0b', color: 'white' },
      confirmed: { backgroundColor: '#10b981', color: 'white' },
      cancelled: { backgroundColor: '#ef4444', color: 'white' },
      completed: { backgroundColor: '#6b7280', color: 'white' }
    };
    return {
      style: statusColors[event.resource.status] || statusColors.pending
    };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'cancelled': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'pending': return <AlertCircle className="h-4 w-4 text-orange-600" />;
      default: return <CheckCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      cancelled: 'Cancelada',
      completed: 'Completada'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-16 h-16 border-4 rounded-full animate-spin" 
             style={{borderColor: '#D4AF37', borderTopColor: 'transparent'}}></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <CalendarIcon style={{color: '#D4AF37'}} />
            Reservas
          </h1>
          <p className="text-gray-600">Gestión de reservas y calendario</p>
        </div>
        <div className="flex gap-3">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setView('calendar')}
              className={`px-4 py-2 rounded-md transition-colors ${
                view === 'calendar' 
                  ? 'text-white' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              style={view === 'calendar' ? {background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'} : {}}
            >
              <CalendarIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-4 py-2 rounded-md transition-colors ${
                view === 'list' 
                  ? 'text-white' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              style={view === 'list' ? {background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'} : {}}
            >
              <List className="h-5 w-5" />
            </button>
          </div>
          
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-transform hover:scale-105"
            style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
          >
            <Plus className="h-5 w-5" />
            Nueva Reserva
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">Total Reservas</p>
          <p className="text-2xl font-bold text-gray-800">{reservations.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">Pendientes</p>
          <p className="text-2xl font-bold text-orange-600">
            {reservations.filter(r => r.status === 'pending').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">Confirmadas</p>
          <p className="text-2xl font-bold text-green-600">
            {reservations.filter(r => r.status === 'confirmed').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">Con Transporte</p>
          <p className="text-2xl font-bold text-blue-600">
            {reservations.filter(r => r.needs_transport).length}
          </p>
        </div>
      </div>

      {/* Calendar View */}
      {view === 'calendar' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" style={{height: '600px'}}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={(event) => handleEdit(event.resource)}
            messages={{
              next: "Siguiente",
              previous: "Anterior",
              today: "Hoy",
              month: "Mes",
              week: "Semana",
              day: "Día",
              agenda: "Agenda",
              date: "Fecha",
              time: "Hora",
              event: "Reserva",
            }}
          />
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha/Hora</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Experiencia</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reservations.map((reservation) => (
                  <tr key={reservation.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{reservation.customer_name}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {reservation.customer_phone}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {reservation.people} personas
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{reservation.date}</div>
                      <div className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {reservation.time}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {experienceTypes.find(e => e.value === reservation.experience_type)?.label}
                      </div>
                      {reservation.needs_transport && (
                        <div className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />
                          Con transporte
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={reservation.status}
                        onChange={(e) => handleStatusChange(reservation.id, e.target.value)}
                        className="text-sm border-none focus:ring-0 cursor-pointer"
                      >
                        <option value="pending">Pendiente</option>
                        <option value="confirmed">Confirmada</option>
                        <option value="cancelled">Cancelada</option>
                        <option value="completed">Completada</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleEdit(reservation)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(reservation.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal - Debido a espacio, lo dejo simplificado. Se puede expandir */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8"
          >
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingReservation ? 'Editar Reserva' : 'Nueva Reserva'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                  <input
                    type="text"
                    required
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono *</label>
                  <input
                    type="tel"
                    required
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Personas *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.people}
                    onChange={(e) => setFormData({...formData, people: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha *</label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hora *</label>
                  <input
                    type="time"
                    required
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Experiencia *</label>
                <select
                  value={formData.experience_type}
                  onChange={(e) => setFormData({...formData, experience_type: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  {experienceTypes.map(exp => (
                    <option key={exp.value} value={exp.value}>{exp.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.needs_transport}
                  onChange={(e) => setFormData({...formData, needs_transport: e.target.checked})}
                  className="rounded"
                  style={{accentColor: '#D4AF37'}}
                />
                <label className="text-sm font-medium text-gray-700">Necesita transporte</label>
              </div>

              {formData.needs_transport && (
                <div className="grid md:grid-cols-2 gap-4 pl-6 border-l-2" style={{borderColor: '#D4AF37'}}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Punto de recogida</label>
                    <input
                      type="text"
                      value={formData.transport_pickup}
                      onChange={(e) => setFormData({...formData, transport_pickup: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Punto de entrega</label>
                    <input
                      type="text"
                      value={formData.transport_dropoff}
                      onChange={(e) => setFormData({...formData, transport_dropoff: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="pending">Pendiente</option>
                  <option value="confirmed">Confirmada</option>
                  <option value="cancelled">Cancelada</option>
                  <option value="completed">Completada</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mensaje/Notas</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 text-white px-6 py-3 rounded-lg font-semibold shadow-lg"
                  style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
                >
                  {editingReservation ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Reservations;
